import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOperationalExcellenceNavigationItems } from 'src/utilities/RouteConfig';
import { FinTechOpsHref } from '../FinTechOpsConstants';
import { useOEReportLinkVisible } from '../oe-report/access/useOEReportLinkVisible';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';

const NAV_ORDER: string[] = [
  FinTechOpsHref.oeInsights,
  FinTechOpsHref.userMetrics,
  FinTechOpsHref.tickets,
  FinTechOpsHref.kpiMetrics,
  FinTechOpsHref.costMetrics,
  FinTechOpsHref.sas,
  FinTechOpsHref.sasV2,
  FinTechOpsHref.policyEngine,
  FinTechOpsHref.dogma,
  FinTechOpsHref.riskRemediator,
  FinTechOpsHref.upgradeMedic,
  FinTechOpsHref.dataMapping,
  FinTechOpsHref.oeReportGenerator,
  FinTechOpsHref.pipelineOEDashboard,
  FinTechOpsHref.oeReport
];

export const OESideNavigation: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Limited preview: the link is shown only to allowlisted aliases, though the route
  // stays reachable by URL.
  const showOEReport = useOEReportLinkVisible();

  const items = useMemo<SideNavigationProps.Item[]>(() => {
    const allItems = getOperationalExcellenceNavigationItems().filter((item): item is SideNavigationProps.Link => item.type === 'link');
    const ordered = NAV_ORDER.map((href) => allItems.find((item) => item.href === href)).filter(Boolean) as SideNavigationProps.Link[];
    return ordered.filter((item) => item.href !== FinTechOpsHref.oeReport || showOEReport);
  }, [showOEReport]);

  const handleFollow: SideNavigationProps['onFollow'] = (event) => {
    event.preventDefault();
    navigate(event.detail.href);
  };

  // Highlight the nav item whose href is the longest prefix of the current path, so tab
  // sub-routes (e.g. /operational-excellence/user-metrics-v2/portfolio) still highlight their
  // parent item (/operational-excellence/user-metrics). Falls back to the exact pathname.
  const activeHref = useMemo(() => {
    const linkHrefs = items.filter((item): item is SideNavigationProps.Link => item.type === 'link' && !!item.href).map((item) => item.href);
    const matches = linkHrefs.filter((href) => pathname === href || pathname.startsWith(href + '/'));
    if (matches.length === 0) return pathname;
    return matches.reduce((longest, href) => (href.length > longest.length ? href : longest));
  }, [items, pathname]);

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{ text: Titles.TITLE_OP_EX, href: FinTechOpsHref.finTechOps }}
      items={items}
      onFollow={handleFollow}
    />
  );
};
